import { registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { INVALID_INPUT, InlineEditor } from '../InlineEditor'
import { declaredUnit, formatValue } from '../../views/format'
import { getLocale } from '../../locale'

/** E4 · number content — right-aligned, tabular figures. Content only.
 *  Uses the tenant locale from the ONE seam (locale.ts) so da-DK shows
 *  "1.566" (period = thousands separator) instead of "1,566" (en-US). */
function NumberContent({ value, config }: CellContentProps) {
  if (value == null || value === '') {
    return <span className="text-muted-foreground">—</span>
  }
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) {
    return <span className="text-muted-foreground">—</span>
  }
  // SEN-1796: a number attribute DECLARING a unit (config.unit_composition,
  // e.g. size_sqm's m²) shows it — the declared unit was previously dropped
  // by every real number cell. Plain numbers stay plain.
  const unit = declaredUnit(value, config)
  return (
    <span className="tabular-nums">
      {formatValue(n, getLocale())}
      {unit && <span className="ml-1 text-muted-foreground">{unit}</span>}
    </span>
  )
}

/** number editor — raw numeric input; empty → null (intentional clear);
 *  anything non-numeric is INVALID and refused by the shared editor (PR275
 *  review blocker: returning props.value on NaN silently committed the old —
 *  or, with an unloaded value, an UNDEFINED — draft as if accepted, which
 *  could clear the stored cell with an HTTP 200 and no refusal anywhere). */
export function NumberEditor(props: CellEditorProps) {
  return (
    <InlineEditor
      {...props}
      inputProps={{ inputMode: 'decimal' }}
      parse={(s) => {
        const t = s.trim()
        if (t === '') return null
        const n = Number(t)
        // PR275 8609 review: Number.isNaN let Infinity/-Infinity and
        // exponent overflow (1e309 → Infinity) through, and JSON encodes
        // non-finite as null — a silent clearing write. FINITE is the
        // contract: everything else is refused.
        return Number.isFinite(n) ? n : INVALID_INPUT
      }}
    />
  )
}

registerWidget('number', { read: NumberContent, write: NumberEditor, align: 'right', defaultWidth: 110, showsUnit: true })
